import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Space, Modal, Select, Button, Table } from  'antd';
import { CheckCircleOutlined, ClockCircleOutlined, CloudDownloadOutlined, PlusOutlined, RedoOutlined, SyncOutlined } from '@ant-design/icons'
import SpinnerCentered from '../../components/SpinnerCentered/SpinnerCentered';
import useHasError from '../../hooks/useHasError'; 
import useDataWarehouseExportTypes from '../../gql/hooks/useDataWarehouseExportTypes';
import useDataWarehouseExport from '../../gql/hooks/useDataWarehouseExport';
import useDataWarehouseCreateReport from '../../gql/hooks/useDataWarehouseCreateReport';
import './styles.less'; 
import { Layout } from 'antd';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import { Card } from 'antd';
import { Routes } from '../../../routes';
import DATA_WAREHOUSE_EXPORTS from '../../gql/queries/DATA_WAREHOUSE_EXPORTS';
import { useLazyQuery } from '@apollo/client';
import { Tag } from 'antd';
import moment from 'moment';
import { Form } from 'antd';

function titleCase(text) {
    return text.toLowerCase().split('_').map(w => w.charAt(0).toUpperCase() + w.substring(1)).join(' ')
}

const formatChoices = [ { value: 'csv', label: 'CSV'}, {value: 'json', label: 'JSON'}];

const numberFormatter = new Intl.NumberFormat( 'en-US' );

const CreateReportModal = ({ createReportCb, typeOptions }) => {

    const [form] = Form.useForm();
    
    const { createReport } = useDataWarehouseCreateReport();
    const [ showModal, setShowModal ] = useState( false );
    const [ isSaving, setIsSaving ] = useState( false );

    async function handleCreateReport(values) {

        setIsSaving(true)       
        
        const response = await createReport({
            ...values
        })

        setIsSaving(false)

        if(response.success) {
            createReportCb()
            setShowModal(false)
        } else {
            console.error( 'Error creating report' );
        }

    }
    
    const handleCreateClick = async () => {

        form.validateFields()
            .then(handleCreateReport)
            .catch(info => console.log('failed:', info))
    };
    
    const handleCancelClick = () => {
        setShowModal(false)
    };

    const handleOpen = () => {
        setShowModal(true)
        form.resetFields();
    }

    return <>
        <Button  
            icon={ <PlusOutlined/> }
            onClick={ () => handleOpen() }
        >
            Create Report
        </Button>
        <Modal
            centered
            width={ 800 }
            height={ 400 }
            className='create-report-modal'
            title="Create Report"
            open={ showModal }
            onOk={ () => form.submit() }
            onCancel={ handleCancelClick } 
            confirmLoading={ isSaving }
            okText={ 'Create' }  
        >
            <p>Select the type of report you want to create and click save. </p>

            <Form
                name='create-report'
                layout='vertical'
                form={form}
                initialValues={{ exportType: undefined, format: formatChoices[0].value}}
                onFinish={handleCreateClick}
                >
            
                <div className='selectors'>
                    <Form.Item
                        form={form}
                        label='Report Type'
                        name='exportType'
                        htmlFor='Report Type'
                        rules={[{required: true, message: 'Report type is required'}]}
                        >

                        <Select 
                            showSearch
                            placeholder='Select type...'
                            options={ typeOptions }
                        />
                    </Form.Item>
                    <Form.Item
                        label='Export Format'
                        name='format'
                        >
                        <Select 
                            showSearch
                            options={ formatChoices }
                        />
                    </Form.Item>
                </div>

            </Form>
        </Modal>
    </>
}

// Applied to every column except the greedy name column: keeps headers and
// cell contents on a single line so they aren't compressed into wrapping.
const noWrap = {
    onHeaderCell: () => ( { style: { whiteSpace: 'nowrap' } } ),
    onCell: () => ( { style: { whiteSpace: 'nowrap' } } ),
};

const StaticReportingPage = ( ) => {
    const { hasError } = useHasError( { items: [ typesError ] } );

    const { types, typesLoading, typesError } = useDataWarehouseExportTypes();
    const {loadExport} = useDataWarehouseExport();
    const [ fetchExportsList, { data, loading, error } ] = useLazyQuery( DATA_WAREHOUSE_EXPORTS );

    const [prevSearchVars, setPrevSearchVars] = useState();
    const [refreshing, setRefreshing] = useState(false);
    const disabled = useMemo(() => loading || typesLoading || typesError || refreshing, [loading, typesLoading, typesError, refreshing])

    const [selectedFormats, setSelectedFormats] = useState([])
    const [selectedExportTypes, setSelectedExportTypes] = useState([])
    const [selectedStatuses, setSelectedStatuses] = useState([])
    const [selectedDir, setSelectedDir] = useState('desc')
    const [selectedSort, setSelectedSort] = useState('createdAt')
    const [currentPage, setCurrentPage] = useState(1);

    const typesChoices = useMemo( () => types?.map( t => ({ value: t.name, label: titleCase(t.name), id: t.id})) ?? [], [types])
    const statusChoices = [ { value: 'COMPLETED', label: 'Completed'}, {value: 'PENDING', label: 'Pending'}, {value: 'IN_PROGRESS', label: 'In Progress'}];

    const isNewFilters = useMemo(() => {
        if(!prevSearchVars) return true;
        const currVars = getUpdatedVars();

        const isEqual = (a, b) => {
            return a == b || (Array.isArray(a) && Array.isArray(b) && a.length == b.length && a.every(x => b.includes(x)))
        } 

        return !(isEqual(currVars.formats, prevSearchVars.formats) && 
            isEqual(currVars.exportTypes, prevSearchVars.exportTypes) && 
            isEqual(currVars.statuses, prevSearchVars.statuses));

    }, [prevSearchVars, selectedFormats, selectedExportTypes, selectedStatuses])

    function getUpdatedVars(newVars = null) {
        return {
            formats: newVars?.formats ?? selectedFormats,
            exportTypes: newVars?.exportTypes ?? selectedExportTypes,
            statuses: newVars?.statuses ?? selectedStatuses,
            page: newVars?.page ?? currentPage,
            limit: 10,
            orderBy: selectedSort,
            orderDir: newVars?.orderDir ?? selectedDir
        }
    }

    function getSearchVars(newVars = null) {
        const exportTypes = newVars == null || newVars.exportTypes.length == 0 ? typesChoices.map(t => t.value) : newVars.exportTypes;
        return {
            ...newVars,
            exportTypes
        }
    }

    function search(newVars) {

        const updatedVars = getUpdatedVars(newVars)

        setPrevSearchVars(updatedVars);

        const searchVars = getSearchVars(updatedVars);

        fetchExportsList({
            variables: {options: searchVars},
            fetchPolicy: 'network-only'
        })

        setRefreshing(false)
    }

    const currentData = useMemo(() => {
        if(!data) return [];
        const tableData = data?.dataWarehouseExports?.edges?.map(e => e?.node) ?? [];
        setRefreshing(false);
        return tableData;
    }, [data])

    useEffect( () => {
        if ( typesChoices?.length > 0 ) {
            search()
        }
    }, [ typesChoices ] );
    
    const handleDownload = async ( item ) => {
        const response = await loadExport( item.id );

        if ( response.data?.dataWarehouseExport?.node?.url ) {
            const url = response.data?.dataWarehouseExport?.node?.url;
            window.open( url, '_blank' );
        } else {
            console.error( 'Error getting url' );
        }
    };

    const handleTableChange = ( pagination, filters, sorter ) => {      
        const order =  sorter.order == 'ascend' ? 'asc' : 'desc';
        setCurrentPage( pagination.current );
        setSelectedDir(order);
        setSelectedSort( sorter.column?.dataIndex );

        search({...prevSearchVars, page: pagination.current, orderDir: order, orderBy: sorter.column?.dataIndex})
    };

    const applyFilters = () => {
        setCurrentPage(1)
        search({ page: 1 })
    };

    const columns =  [ 
        {
            key: 'name',
            dataIndex: 'exportTypeId',
            title: 'Type', 
            className: 'name-column',
            width: '100%',
            fixed: 'left',
            render: (exportTypeId) => typesChoices?.find(t => t.id == exportTypeId)?.label
        },
        {
            key: 'rowCount',
            dataIndex: 'rowCount',
            title: 'Row Count',
            render: ( rowCount ) => rowCount != null ? numberFormatter.format( rowCount ) : ''
        },
        {
            key: 'fileSizeHuman',
            dataIndex: 'fileSizeHuman',
            title: 'File Size',
            render: ( fileSizeHuman ) => fileSizeHuman ?? ''
        },
        {
            key: 'status',
            dataIndex: 'status',
            title: 'Status',
            fixed: 'left',
            className: 'status-column',
            render: ( status ) => status == 'COMPLETED' ? <Tag color='green'><CheckCircleOutlined /> {titleCase(status)}</Tag> :
                status == 'IN_PROGRESS' ? <Tag color='blue'><SyncOutlined spin/> {titleCase(status)}</Tag> :
                <Tag color='grey'><ClockCircleOutlined /> {titleCase(status)}</Tag>
        },
        {
            key: 'format',
            dataIndex: 'format',
            title: 'Format',
            fixed: 'left',
            render: ( format ) => format == 'json' ? <Tag color='purple'>JSON</Tag> :
                format == 'csv' ? <Tag color='orange'>CSV</Tag> : <Tag color='grey'>{format.toUpperCase()}</Tag>
        },
        {
            key: 'createdAt',
            dataIndex: 'createdAt',
            title: 'Created At', 
            sortKey: 'createdAt',
            defaultSortOrder: 'descend',
            sortDirections: ['descend','ascend'],
            render: (time) => moment(time).format('lll'),
            sorter: () => 0,
        },
        {
            key: 'actions', 
            title: 'Actions',
            fixed: 'right', 
            width: '100px',
            render: ( item ) => 
                <Space>
                    { item.status === 'COMPLETED' &&
                        <Button 
                            type="link" 
                            icon={ <CloudDownloadOutlined /> } 
                            onClick= { () => handleDownload( item ) }
                        />
                    }
                </Space>
        } 
    ];

    function onReportCreation() {
        applyDefaultSearch();
    }

    function applyDefaultSearch() {
        setCurrentPage(1);
        setSelectedSort('createdAt')
        setSelectedDir('desc')
        setSelectedFormats([]);
        setSelectedExportTypes([]);
        setSelectedStatuses([]);
        search({ page: 1, orderDir: 'desc', orderBy: 'createdAt', formats: [], exportTypes: [], statuses: [] })

    }

    function onRefresh() {

        setRefreshing(true);
        search(prevSearchVars);
    }

    const breadcrumbs = [
            { name: 'Course Studio', path: Routes.Home.buildPath() },
            { name: 'Analytics', path: Routes.Reporting.buildPath() },
            { name: 'Static Reporting'}
        ];

    return (
        <Layout className="static-reporting-page">
            
            { /* Page header breadcrumbs */ }
            <Breadcrumbs crumbs={ breadcrumbs }/>

            <Card
                className="static-reporting-content"
                title="Static Reporting"
                extra={<Button disabled={disabled} onClick={onRefresh} type='primary'>{refreshing ? <SyncOutlined spin /> : <RedoOutlined />} Refresh</Button>}
            >   
                <div className="reports-list">
                    { /* Loading spinner */ }
                    { typesLoading &&
                        <SpinnerCentered/>
                    }
                    
                    { /* Error Alert */ }
                    { hasError && 
                        <Alert 
                            type="error"
                            message="Error"
                            description="An unknown error occurred. Please try again later."
                        />
                    }

                    { /* Reports list */ }
                    { !typesLoading && !hasError &&
                        <>
                            { error && 
                                <Alert 
                                    type="error"
                                    message="Error"
                                    description={
                                        <>
                                            <p>An unknown error occurred. Please try again later.</p>
                                        </>
                                    }
                                />
                            }
                        <div className="toolbar">
                            <Space className='filters'>

                                <Select  
                                    mode="multiple" 
                                    allowClear
                                    disabled={disabled}
                                    placeholder="Filter by Type"
                                    value={ selectedExportTypes }
                                    onChange={ ( item ) => setSelectedExportTypes( item ) }
                                    options={ typesChoices }
                                />

                                <Select  
                                    mode="multiple" 
                                    allowClear
                                    disabled={disabled}
                                    placeholder="Filter by Format"
                                    value={ selectedFormats }
                                    onChange={ ( item ) => setSelectedFormats( item ) }
                                    options={ formatChoices}
                                />
                                
                                <Select  
                                    mode="multiple" 
                                    allowClear
                                    disabled={disabled}
                                    placeholder="Filter by Status"
                                    value={ selectedStatuses }
                                    onChange={ ( item ) => setSelectedStatuses( item ) }
                                    options={ statusChoices }
                                />

                                <Button  
                                    type="primary"  
                                    disabled={disabled || !isNewFilters}
                                    onClick={ applyFilters }
                                >
                                    Apply Filters
                                </Button>
                            </Space>
                            
                            <Space className='create'>
                                <CreateReportModal typeOptions={typesChoices} formatOptions={formatChoices} createReportCb={onReportCreation} />

                            </Space>
                            
                        </div>

                        <Alert description={<span><b>Important Notice:</b> Our data warehouse refreshes enrollment data hourly and person data daily at 3:30 AM UTC. The latest enrollment and person data may not be reflected in reports until after scheduled imports have run.</span>} type='warning' />

                        <Table
                            tableLayout="auto"
                            rowKey='id'
                            loading= { loading }
                            columns={ columns.map( c => c.key === 'name' ? c : { ...noWrap, ...c } ) }
                            dataSource={ currentData }
                            onChange={ handleTableChange }
                            pagination={ { 
                                total: data?.dataWarehouseExports?.pagination?.totalCount,
                                current: currentPage,
                                showQuickJumper: false,
                                showSizeChanger: false
                            } }
                        />   
                        </>
                    }
                </div>
            </Card>
        </Layout>
    );
};

export default StaticReportingPage;
